import React from 'react';
import { getIntl } from '../../../utils/intl';
import { supportEmail } from '../../../utils/constants';
import useReportError, { UseReportErrorReturnValue } from '../../../hooks/api/useReportERror';
import { notifyBugsnag } from '../../../utils/bugsnag';

const _intl = getIntl('ErrorBoundary', {
    errorBoundaryMessage: 'An error occurred',
    ifPersistent: `If this issue persists, please contact support at ${supportEmail}.`,
    emailSupport: 'Email Support {email}',
    emailSubject: 'Error in the app',
    emailBody: 'Hello, I encountered an error in the app.',
});

export type ErrorBoundaryProps = {
    children: React.ReactNode;
    renderError?: (onDismiss: () => void) => React.ReactNode;
};

type ErrorBoundaryState = {
    hasError: boolean;
    childKey: number;
};

type ErrorBoundaryClassComponentProps = ErrorBoundaryProps & Pick<UseReportErrorReturnValue, 'reportError'>;

class ErrorBoundaryClassComponent extends React.Component<ErrorBoundaryClassComponentProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryClassComponentProps) {
        super(props);
        this.state = { hasError: false, childKey: 0 };
    }

    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        this.props.reportError({
            message: error.message || '',
            errorInfo: ((errorInfo.componentStack || '') as string).substring(0, 1000),
            url: window?.location?.href || '',
        });
        console.error({ error, errorInfo });
        notifyBugsnag(error);
        // You can also log the error to an error reporting service
        // logErrorToMyService(error, errorInfo);
    }

    render() {
        /*
        if (this.state.hasError) {
            const onDismiss = () => {
                this.setState({ hasError: false, childKey: this.state.childKey + 1 });
            };

            // You can render any custom fallback UI
            return this.props.renderError ? (
                this.props.renderError(onDismiss)
            ) : (
                <Stack vertical fullWidth fullHeight align="center" justify="center">
                    <Alert
                        type="error"
                        title={intl('errorBoundaryMessage')}
                        message={intl('ifPersistent')}
                        actionItems={[
                            {
                                title: intl('emailSupport', { email: supportEmail }),
                                onPress: () => {
                                    Linking.openURL(
                                        `mailto:${supportEmail}?subject=${encodeURIComponent(
                                            intl('emailSubject'),
                                        )}&body=${encodeURIComponent(intl('emailBody'))}`,
                                    ).catch(() => {
                                        console.error('Failed to open email');
                                    });
                                },
                            },
                        ]}
                    />
                </Stack>
            );
        }
            */

        return this.props.children;
    }
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
    const { reportError } = useReportError();
    return <ErrorBoundaryClassComponent {...props} reportError={reportError} />;
};

export default React.memo(ErrorBoundary);
